import {
  decryptHistoryPatch,
  encryptHistoryPatch,
  encryptVaultForPairing,
  generateDeviceLinkKey,
  decryptVaultFromPairing,
  importDeviceLinkKey,
  generateEphemeralKeyPair,
} from './deviceLinkCrypto.ts'
import {
  buildDeviceVaultPayload,
  buildHistoryPatchPayload,
  applyDeviceVaultPayload,
  applyHistoryPatch,
  type DeviceVaultPayload,
} from './deviceVault.ts'
import {
  buildDeviceLinkUrl,
  deviceSyncChannelBase,
  parseDeviceLinkQr,
  parseDeviceLinkSessionId,
  type DeviceLinkQrPayload,
} from './deviceLinkProtocol.ts'
import {
  createDeviceSyncSession,
  fetchDeviceSyncSessionInfo,
  listenDeviceSyncSession,
  pushEncryptedVault,
  pushHistorySyncToDevice,
  registerLinkedDeviceOnServer,
} from './deviceLinkClient.ts'
import {
  defaultDeviceLabel,
  detectPlatform,
  getLocalDeviceId,
  getDeviceLinkKeyB64,
  listLinkedDevices,
  registerSelfDevice,
  saveDeviceLinkKeyB64,
  upsertLinkedDevice,
} from './deviceLinkStore.ts'
import { getSessionToken } from '../storage/sessionStore.ts'
import { renderQrDataUrl } from '../pairing/qrCode.ts'

export type StatusCallback = (status: string) => void

/** Fase 1 — Dispositivo nuovo (PC): genera QR e attende vault. */
export async function startReceiverPairing(onStatus: StatusCallback): Promise<{
  qrDataUrl: string
  qrText: string
  waitForComplete: () => Promise<void>
  cancel: () => void
  offline?: boolean
}> {
  onStatus('[GENERATING EPHEMERAL KEYS...]')
  const ephemeral = await generateEphemeralKeyPair()
  const deviceId = await getLocalDeviceId()

  let sessionId = ''
  let offline = false

  try {
    onStatus('[OPENING SECURE CHANNEL...]')
    const created = await Promise.race([
      createDeviceSyncSession(deviceId, ephemeral.publicKeyBase64),
      new Promise<never>((_, reject) => {
        window.setTimeout(() => reject(new Error('device_sync_timeout')), 5_000)
      }),
    ])
    sessionId = created.sessionId
  } catch (err) {
    console.error('[ZION] DeviceSync.create failed — QR offline locale', err)
    offline = true
    sessionId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `offline-${Date.now().toString(36)}`
    onStatus('[OFFLINE — QR locale; tocca Riconnetti]')
  }

  const qrText = buildDeviceLinkUrl(sessionId)
  onStatus(offline ? '[RENDERING OFFLINE QR...]' : '[RENDERING QR...]')
  const qrDataUrl = await renderQrDataUrl(qrText)

  let stopListen = (): void => {}
  let resolveDone: (() => void) | null = null
  let rejectDone: ((err: Error) => void) | null = null

  const completePromise = new Promise<void>((resolve, reject) => {
    resolveDone = resolve
    rejectDone = reject
  })

  if (!offline) {
    stopListen = listenDeviceSyncSession(sessionId, {
      onStatus,
      onError: (msg) => rejectDone?.(new Error(msg)),
      onVault: async (encrypted, senderEphemeralPubKey) => {
        try {
          onStatus('[VAULT RECEIVED — DECRYPTING...]')
          const plain = await decryptVaultFromPairing(ephemeral.privateKey, senderEphemeralPubKey, encrypted)
          const vaultPayload = JSON.parse(new TextDecoder().decode(plain)) as DeviceVaultPayload
          onStatus('[IMPORTING IDENTITY...]')
          await applyDeviceVaultPayload(vaultPayload)
          const session = vaultPayload.vault.identity as { sessionToken?: string } | undefined
          const sessionToken = String(session?.sessionToken ?? '').trim()
          if (sessionToken) {
            await registerLinkedDeviceOnServer({
              sessionToken,
              deviceId,
              label: defaultDeviceLabel(),
              platform: detectPlatform(),
            })
          }
          onStatus('[SYNC COMPLETE]')
          stopListen()
          resolveDone?.()
        } catch (err) {
          rejectDone?.(err instanceof Error ? err : new Error(String(err)))
        }
      },
    })
  }
  // Offline: QR locale già mostrato; waitForComplete resta pending fino a Riconnetti/cancel.

  return {
    qrDataUrl,
    qrText,
    offline,
    waitForComplete: () => completePromise,
    cancel: () => {
      stopListen()
      rejectDone?.(new Error('device_sync_cancelled'))
    },
  }
}

async function resolveDeviceLinkQr(qrRaw: string, onStatus: StatusCallback): Promise<DeviceLinkQrPayload> {
  const legacy = parseDeviceLinkQr(qrRaw)
  if (legacy) return legacy

  const sessionId = parseDeviceLinkSessionId(qrRaw)
  if (!sessionId) throw new Error('QR non valido o scaduto.')

  onStatus('[FETCHING SESSION...]')
  const info = await fetchDeviceSyncSessionInfo(sessionId)
  if (!info) throw new Error('Sessione non trovata o scaduta.')
  if (Date.now() > info.expiresAt) throw new Error('Sessione scaduta — rigenera il QR sul PC.')

  return {
    v: 1,
    sessionId: info.sessionId,
    pubKey: info.receiverEphemeralPubKey,
    channelBase: deviceSyncChannelBase(),
    deviceId: info.receiverDeviceId,
    exp: info.expiresAt,
  }
}

/** Fase 1 — Dispositivo principale: scansiona QR e invia vault cifrato. */
export async function sendVaultToNewDevice(qrRaw: string, onStatus: StatusCallback): Promise<void> {
  const qr = await resolveDeviceLinkQr(qrRaw, onStatus)

  onStatus('[STABILISHING SECURE TUNNEL...]')
  const ephemeral = await generateEphemeralKeyPair()
  const { base64: deviceLinkKeyB64 } = await generateDeviceLinkKey()
  await saveDeviceLinkKeyB64(deviceLinkKeyB64)

  onStatus('[DUMPING ENCRYPTED VAULT...]')
  const vaultPayload = await buildDeviceVaultPayload(deviceLinkKeyB64)
  const plain = new TextEncoder().encode(JSON.stringify(vaultPayload))
  const encrypted = await encryptVaultForPairing(ephemeral.privateKey, qr.pubKey, plain)

  onStatus('[TRANSFERRING ENCRYPTED VAULT...]')
  const senderDeviceId = await getLocalDeviceId()
  await registerSelfDevice()
  await upsertLinkedDevice({
    deviceId: qr.deviceId,
    label: 'PC collegato',
    platform: 'desktop',
    linkedAt: Date.now(),
    isSelf: false,
  })

  const ok = await pushEncryptedVault(qr.sessionId, encrypted, ephemeral.publicKeyBase64, senderDeviceId)
  if (!ok) throw new Error('Invio vault fallito.')

  const sessionToken = (await getSessionToken())?.trim()
  if (sessionToken) {
    await registerLinkedDeviceOnServer({
      sessionToken,
      deviceId: senderDeviceId,
      label: defaultDeviceLabel(),
      platform: detectPlatform(),
    })
    await registerLinkedDeviceOnServer({
      sessionToken,
      deviceId: qr.deviceId,
      label: 'PC collegato',
      platform: 'desktop',
    })
  }

  onStatus('[SYNC COMPLETE]')
}

/** Fase 3 — Mobile spinge cronologia al PC target. */
export async function pushHistoryToDevice(targetDeviceId: string, onStatus: StatusCallback): Promise<void> {
  onStatus('[PREPARING ENCRYPTED PATCH...]')
  const linkKeyB64 = await getDeviceLinkKeyB64()
  if (!linkKeyB64) throw new Error('Device-Link Key assente. Collega prima un dispositivo.')

  const sessionToken = (await getSessionToken())?.trim()
  if (!sessionToken) throw new Error('Sessione non attiva.')

  const patch = await buildHistoryPatchPayload()
  const deviceLinkKey = await importDeviceLinkKey(linkKeyB64)
  const encrypted = await encryptHistoryPatch(
    deviceLinkKey,
    new TextEncoder().encode(JSON.stringify(patch)),
  )

  onStatus('[PUSHING TO TARGET DEVICE...]')
  const fromDeviceId = await getLocalDeviceId()
  const ok = await pushHistorySyncToDevice({
    sessionToken,
    fromDeviceId,
    targetDeviceId,
    encrypted,
  })
  if (!ok) throw new Error('Push cronologia fallito — PC offline?')
  onStatus('[SYNC COMPLETE]')
}

/** Fase 3 — PC riceve e applica patch cronologia. */
export async function applyIncomingHistoryPush(encrypted: string, onStatus: StatusCallback): Promise<number> {
  onStatus('[DECRYPTING HISTORY PATCH...]')
  const linkKeyB64 = await getDeviceLinkKeyB64()
  if (!linkKeyB64) throw new Error('Device-Link Key assente.')
  const key = await importDeviceLinkKey(linkKeyB64)
  const plain = await decryptHistoryPatch(key, encrypted)
  const root = JSON.parse(new TextDecoder().decode(plain)) as Record<string, unknown>
  onStatus('[MERGING INDEXEDDB...]')
  const count = await applyHistoryPatch(root)
  onStatus('[SYNC COMPLETE]')
  return count
}

export {
  parseDeviceLinkQr,
  parseDeviceLinkSessionId,
  buildDeviceLinkUrl,
  listLinkedDevices,
  type DeviceLinkQrPayload,
}
